import axios, {isAxiosError } from 'axios';
import { TruePlanVars } from '../TruePlan';
import dataService from './data.service';
import { emitGlobalToast } from './toastBus';
import { HTTP_DEFAULT_TIMEOUT_MS, HTTP_NETWORK_TOAST_THROTTLE_MS } from '../constants/http';

export default class Http {

    static _interceptorsRegistered = false;

    // Throttle state for the network/timeout "server busy" toast (M-04): suppress an identical
    // message fired again within HTTP_NETWORK_TOAST_THROTTLE_MS so a stampede shows one alert.
    static _lastNetworkToast = { message: null, at: 0 };
    static _maybeEmitNetworkToast(message) {
        const now = Date.now();
        const last = Http._lastNetworkToast;
        if (last.message === message && (now - last.at) < HTTP_NETWORK_TOAST_THROTTLE_MS) {
            return;
        }
        Http._lastNetworkToast = { message, at: now };
        emitGlobalToast(message, 'warning');
    }

    static setTokenFunctions(getAccessTokenSilently, getIdTokenClaims, logout) {
        this.getAccessTokenSilently = getAccessTokenSilently;
        this.getIdTokenClaims = getIdTokenClaims;
        this.logout = logout; // Add the logout function
    }

    // Error Handler
    static handleError(error, defaultValue = null) {
        // Canceled/aborted requests are intentional (navigation, plan switch, superseded fetch).
        // Do not log them — they are not real errors.
        if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED') {
            return Promise.resolve(defaultValue);
        }
        // Log status + URL only — never response bodies/headers (may contain PII/financial data).
        if (isAxiosError(error)) {
            if (error.response) {
                console.error('API error', error.response.status, error.config?.url);
            } else {
                console.error('API error (no response)', error.code, error.config?.url);
            }
        } else {
            console.error('Error', error.message);
        }

        return Promise.resolve(defaultValue); // Return a default value (null or desired object) on error
    };

    static async getAccessToken() {
        try {
            if (!this.getAccessTokenSilently) {
                throw new Error("Access token function not set");
            }

            const response = await this.getAccessTokenSilently(); // Replace this with your method to retrieve the access token
            return response; // Return the access token
        } catch (error) {
            console.error('Failed to retrieve access token:', error);
            return null; // Return null or handle the error appropriately
        }
    }

    static async getIdToken() {
        try {
            if (!this.getIdTokenClaims) {
                throw new Error("Access token function not set");
            }

            const claims = await this.getIdTokenClaims();
            const response = claims.__raw;
            return response; // Return the access token
        } catch (error) {
            console.error('Failed to retrieve access token:', error);
            return null; // Return null or handle the error appropriately
        }
    }

    static init() {
        if (Http._interceptorsRegistered) {
            return;
        }
        Http._interceptorsRegistered = true;

        // Requests Interceptor
        axios.interceptors.request.use(
            async (config) => {
                // Fetch both tokens concurrently — they are independent and both normally
                // resolve from the Auth0 in-memory cache. Awaiting them serially doubled the
                // per-request async overhead across every call in the app.
                const [token, accessToken] = await Promise.all([
                    Http.getIdToken(),
                    Http.getAccessToken(),
                ]);

                if (token) {
                    config.headers['Authorization'] = `Bearer ${token}`; // Set the token in the header
                }

                if (accessToken) {
                    config.headers['accesstoken'] = `${accessToken}`;
                }

                // Add organizationId header if not already present and organizationId is not null
                if (!config.headers['organizationId']) {
                    const organizationId = dataService.getSelectedHMGId();
                    if (organizationId != null && organizationId !== undefined && String(organizationId) !== '') {
                        config.headers['organizationId'] = String(organizationId);
                    }
                }

                // Set the base URL for each request
                config.baseURL = TruePlanVars.varServiceURL;

                // Default client-side timeout so a hung/slow backend call cannot occupy a
                // browser connection indefinitely (a driver of the concurrency P0). A per-call
                // config.timeout (e.g. long migration requests) still wins; `timeout: 0` (infinite)
                // is honored — only fill in the default when no timeout was specified at all (L-02).
                if (config.timeout == null) {
                    config.timeout = HTTP_DEFAULT_TIMEOUT_MS;
                }

                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response Interceptor
        axios.interceptors.response.use(
            response => {
                return response;
            },
            async (error) => {
                // Aborted / canceled requests are intentional (navigation, plan switch, a
                // superseded fetch). Never log out, never toast — just propagate silently.
                if (axios.isCancel?.(error) || error.code === 'ERR_CANCELED') {
                    return Promise.reject(error);
                }

                if (error.response) {
                    // Regular response error handling
                    if (error.response.status === 401) {
                        console.error('Unauthorized: 401 error occurred');

                        // If you have the `logout` function available, call it to log the user out
                        if (Http.logout) {
                            Http.logout({ logoutParams: { returnTo: window.location.origin } });
                        }
                    } else if (error.response.status === 403) {
                        // Surface backend-provided 403 message globally so every caller
                        // gets the same toast UX without each one needing to wire it up.
                        const errorData = error.response.data;
                        const errorMessage =
                            errorData?.message ||
                            errorData?.error ||
                            'You do not have permission to perform this action.';
                        emitGlobalToast(errorMessage, 'error');
                        error._tpxToastHandled = true;
                        console.error('Forbidden: 403 error occurred', errorMessage);
                    } else if (error.response.status >= 400 && error.response.status < 500) {
                        console.error('4xx error occurred, status:', error.response.status);
                    }

                    // Handle other errors (you can choose to call `handleError` or do other things)
                    Http.handleError(error, null);
                } else {
                    // No response received: a timeout or a network/CORS error. This is NOT an
                    // auth failure — do NOT log the user out. (Logging out here caused a mass
                    // re-authentication stampede during a backend slowdown, amplifying the P0.)
                    // Surface a retriable message, but skip it for background/silent calls
                    // (config._silent — e.g. speculative prefetch) and throttle duplicates so a
                    // stampede shows ONE toast, not N (M-04).
                    const isTimeout = error.code === 'ECONNABORTED';
                    const message = isTimeout
                        ? 'The server is busy and is taking longer than usual to respond. Please wait a moment and try again after some time.'
                        : 'We’re having trouble reaching our servers. Please try again in a little while.';
                    if (!error.config?._silent) {
                        Http._maybeEmitNetworkToast(message);
                    }
                    error._tpxToastHandled = true;
                    console.error(isTimeout ? 'Request timeout' : 'Network error', error.config?.url);
                }

                return Promise.reject(error); // Reject the error so that it can be handled further up the chain
            }
        );
    }

    static async GET(url, headers = {}, config = {}) {
        try {
            // Merge headers and config (config can include responseType, etc.)
            const axiosConfig = { ...config, headers: { ...headers, ...(config.headers || {}) } };
            let response = await axios.get(url, axiosConfig);
            return response;
        } catch (error) {
            // Log the error but re-throw it so calling code can handle it
            Http.handleError(error, null);
            throw error; // Re-throw so .catch() in calling code receives the error
        }
    }

    static async POST(url, data = null, config = {}) {
        try {
            let response = await axios.post(url, data, config);
            return response;
        } catch (error) {
            // Log the error but re-throw it so calling code can handle it
            Http.handleError(error, null);
            throw error; // Re-throw so .catch() in calling code receives the error
        }
    }

    static async PUT(url, data = null, config = {}) {
        try {
            let response = await axios.put(url, data, config);
            return response;
        } catch (error) {
            // Log the error but re-throw it so calling code can handle it
            Http.handleError(error, null);
            throw error; // Re-throw so .catch() in calling code receives the error
        }
    }

    static async DELETE(url, config = {}) {
        try {
            let response = await axios.delete(url, config);
            return response;
        } catch (error) {
            // Log the error but re-throw it so calling code can handle it
            Http.handleError(error, null);
            throw error; // Re-throw so .catch() in calling code receives the error
        }
    }
}

Http.init();